import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Sparkles, CheckCircle2, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TagSelector } from "@/components/tags/TagSelector";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface DocumentContent {
  id: string;
  fileName: string;
  filePath: string;
  extractedText: string;
  isEdited: boolean;
}

interface PatientDocumentValidationProps {
  patientId: string;
  documents: DocumentContent[];
  onValidationComplete: () => void;
  onCancel: () => void;
}

export function PatientDocumentValidation({
  patientId,
  documents: initialDocuments,
  onValidationComplete,
  onCancel
}: PatientDocumentValidationProps) {
  const [documents, setDocuments] = useState<DocumentContent[]>(initialDocuments);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [structuredJson, setStructuredJson] = useState<any>(null);
  const [jsonString, setJsonString] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [deletingDocument, setDeletingDocument] = useState<{ id: string, filePath: string, fileName: string } | null>(null);

  const handleTextEdit = (docId: string, newText: string) => {
    setDocuments(prev =>
      prev.map(doc =>
        doc.id === docId
          ? { ...doc, extractedText: newText, isEdited: true }
          : doc
      )
    );
  };

  const handleDeleteDocument = (docId: string, filePath: string, fileName: string) => {
    setDeletingDocument({ id: docId, filePath, fileName });
  };

  const confirmDeleteDocument = async () => {
    if (!deletingDocument) return;

    try {
      // Deletar do banco
      const { error: dbError } = await supabase
        .from('documents_onboarding')
        .delete()
        .eq('id', deletingDocument.id);

      if (dbError) throw dbError;

      // Deletar do storage
      const { error: storageError } = await supabase.storage
        .from('patient-docs')
        .remove([deletingDocument.filePath]);

      if (storageError) {
        console.warn('Erro ao deletar arquivo do storage:', storageError);
      }

      // Atualizar estado local
      setDocuments(prev => prev.filter(doc => doc.id !== deletingDocument.id));
      toast.success(`${deletingDocument.fileName} excluído com sucesso`);

    } catch (err) {
      console.error('Erro ao excluir documento:', err);
      toast.error('Erro ao excluir documento');
    } finally {
      setDeletingDocument(null);
    }
  };

  const generateStructure = async () => {
    const timeoutId = setTimeout(() => {
      toast.error("Tempo esgotado. Tente novamente com menos texto.");
      setIsGenerating(false);
    }, 120000); // 2 minutos de timeout

    let progressInterval: NodeJS.Timeout | null = null;

    try {
      setIsGenerating(true);

      // Consolidate all edited texts
      const consolidatedText = documents
        .map(doc => `# ${doc.fileName}\n${doc.extractedText}`)
        .join("\n\n---\n\n");

      if (!consolidatedText.trim()) {
        toast.error("Nenhum conteúdo para analisar");
        return;
      }

      // Validar tamanho do texto
      if (consolidatedText.length > 50000) {
        toast.warning("Texto muito longo. Pode levar até 2 minutos para processar.");
      }

      // Get patient context
      const { data: patient } = await supabase
        .from("patients_onboarding")
        .select("*")
        .eq("id", patientId)
        .single();

      // Atualizar toast a cada 10 segundos
      toast.loading("Processando com IA... Aguarde.", { id: 'ai-progress' });
      progressInterval = setInterval(() => {
        toast.loading("Ainda processando com IA... Aguarde.", { id: 'ai-progress' });
      }, 10000);

      const { data, error } = await supabase.functions.invoke("validate-clinical-data", {
        body: {
          action: "propose",
          editedLabText: consolidatedText,
          patientContext: patient || {}
        }
      });

      clearInterval(progressInterval);
      clearTimeout(timeoutId);
      toast.dismiss('ai-progress');

      if (error) throw error;

      setStructuredJson(data);
      setJsonString(JSON.stringify(data, null, 2));
      toast.success("Estrutura gerada com sucesso!");
    } catch (err: any) {
      if (progressInterval) clearInterval(progressInterval);
      clearTimeout(timeoutId);
      toast.dismiss('ai-progress');

      console.error("Erro ao gerar estrutura:", err);

      if (err.message?.includes('FunctionsFetchError') || err.message?.includes('timeout')) {
        toast.error("Erro de conexão ou timeout. Verifique sua internet e tente novamente.");
      } else {
        toast.error("Erro ao gerar estrutura via IA");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleJsonEdit = (value: string) => {
    setJsonString(value);
    try {
      const parsed = JSON.parse(value);
      setStructuredJson(parsed);
    } catch {
      // Invalid JSON, don't update structuredJson
    }
  };

  const saveValidated = async () => {
    try {
      setIsSaving(true);

      if (!structuredJson) {
        toast.error("Gere a estrutura antes de salvar");
        return;
      }

      // Validate JSON
      try {
        JSON.parse(jsonString);
      } catch {
        toast.error("JSON inválido. Corrija antes de salvar.");
        return;
      }

      const consolidatedText = documents
        .map(doc => `# ${doc.fileName}\n${doc.extractedText}`)
        .join("\n\n---\n\n");

      const { error: saveError } = await supabase.functions.invoke("validate-clinical-data", {
        body: {
          action: "save",
          patientId,
          editedLabText: consolidatedText,
          structuredJson,
          useOnboardingTables: false // Use main tables
        }
      });

      if (saveError) throw saveError;

      // Update documents_onboarding with final analysis
      for (const doc of documents) {
        await supabase
          .from('documents_onboarding')
          .update({
            extracted_text: doc.extractedText,
            ai_summary: structuredJson.analysis?.summary,
            ai_insights: structuredJson,
            ai_risk_level: structuredJson.analysis?.risk_level,
            has_lab_data: (structuredJson.structured?.tests?.length || 0) > 0,
            status: 'processed'
          })
          .eq('id', doc.id);

        // Save document tags
        if (selectedTagIds.length > 0) {
          const documentTags = selectedTagIds.map(tagId => ({
            document_id: doc.id,
            tag_id: tagId
          }));

          await supabase
            .from('document_tags')
            .insert(documentTags);
        }
      }

      toast.success("Dados validados e salvos com sucesso!");

      // ⭐ AUMENTAR TIMEOUT E MELHORAR FEEDBACK
      toast.info("Processando registros em background...", { duration: 5000 });

      // Aguardar processamento completo (aumentado de 2s para 5s)
      await new Promise(resolve => setTimeout(resolve, 5000));

      onValidationComplete();
    } catch (err) {
      console.error("Erro ao salvar:", err);
      toast.error("Erro ao salvar dados validados");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="shadow-medium">
      <CardContent className="p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-primary mb-2">
            Validação Clínica
          </h2>
          <p className="text-muted-foreground">
            Revise e edite o conteúdo extraído dos documentos antes da análise final
          </p>
        </div>

        {/* Document text areas */}
        <div className="space-y-6 mb-6">
          {documents.map((doc) => (
            <div key={doc.id} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <FileText className="w-4 h-4 text-secondary" />
                  <label className="text-sm font-medium">{doc.fileName}</label>
                  {doc.isEdited && (
                    <span className="text-xs text-muted-foreground">(editado)</span>
                  )}
                </div>
                <button
                  onClick={() => handleDeleteDocument(doc.id, doc.filePath, doc.fileName)}
                  className="p-2 rounded-md hover:bg-destructive/10 text-destructive transition"
                  title="Excluir documento"
                  type="button"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <Textarea
                value={doc.extractedText}
                onChange={(e) => handleTextEdit(doc.id, e.target.value)}
                placeholder="Conteúdo extraído do documento..."
                className="min-h-[200px] font-mono text-sm"
              />
            </div>
          ))}
        </div>

        {/* Generate structure button */}
        {!structuredJson && (
          <Button
            onClick={generateStructure}
            disabled={isGenerating || documents.length === 0}
            className="w-full mb-6"
            size="lg"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Gerando estrutura...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Gerar estrutura via IA
              </>
            )}
          </Button>
        )}

        {/* Structured JSON editor */}
        {structuredJson && (
          <div className="space-y-4 mb-6 p-6 bg-muted/30 rounded-lg border border-muted">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-secondary" />
              <h3 className="font-semibold">Estrutura Gerada</h3>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Resumo</h4>
                <p className="text-sm text-muted-foreground bg-background p-3 rounded">
                  {structuredJson.analysis?.summary || "N/A"}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-2">Nível de Risco</h4>
                <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${structuredJson.analysis?.risk_level === 'alto' ? 'bg-destructive/10 text-destructive' :
                  structuredJson.analysis?.risk_level === 'moderado' ? 'bg-yellow-500/10 text-yellow-600' :
                    'bg-green-500/10 text-green-600'
                  }`}>
                  {structuredJson.analysis?.risk_level || "N/A"}
                </span>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-2">JSON Editável</h4>
                <Textarea
                  value={jsonString}
                  onChange={(e) => handleJsonEdit(e.target.value)}
                  className="font-mono text-xs min-h-[300px]"
                />
              </div>
            </div>

            <TagSelector
              selectedTagIds={selectedTagIds}
              onTagsChange={setSelectedTagIds}
            />

            <div className="flex gap-3">
              <Button
                onClick={onCancel}
                variant="outline"
                className="flex-1"
                size="lg"
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button
                onClick={saveValidated}
                disabled={isSaving}
                className="flex-1"
                size="lg"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Aprovar e salvar
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Modal de Confirmação de Exclusão */}
        <AlertDialog open={!!deletingDocument} onOpenChange={() => setDeletingDocument(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir o documento <strong>{deletingDocument?.fileName}</strong>?
                <br />
                <br />
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteDocument}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Sim, Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
